import React from 'react'
import styled, { RuleSet } from 'styled-components'
import { parseContentWithComponents, ComponentMap, ParsedContentItem } from '../../utils'
import { Tip } from './tip'
import { Card } from './card'
import { Boxes } from './boxes'
import { Schema } from './schema'
// import { testContent } from './test-content'
import { mbSize, Media } from '../../theme'
import { tableStyle } from './styles/table-style'

// 导出组件，方便外部使用
export { Tip } from './tip'
export type { TipProps } from './tip'
export { Card } from './card'
export type { CardProps } from './card'
export { Boxes } from './boxes'
export type { BoxesProps } from './boxes'
export { Schema } from './schema'
export type { SchemaProps } from './schema'

export interface ContentRendererProps {
  htmlContent: string
  componentMap?: ComponentMap
  customCSS?: RuleSet
}

const ContentWrapper = styled.div<{ $customCSS?: RuleSet }>`
  ${tableStyle};
  ul li {
    list-style: disc;
  }
  ol li {
    list-style: decimal;
  }
  ul,
  ol {
    padding-left: 24px;
  }
  li {
    margin: 0 0 16px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  ${Media.lessThan(Media.small)} {
    ul,
    ol {
      padding-left: ${mbSize(40)};
    }
    li {
      margin: 0 0 ${mbSize(32)} 0;
    }
  }
  ${({ $customCSS }) => $customCSS || ''}
`

/**
 * 渲染混合内容（HTML 字符串和 React 组件）
 */
export const ContentRenderer: React.FC<ContentRendererProps> = ({
  htmlContent,
  componentMap: customComponentMap,
  customCSS,
}) => {
  if (!htmlContent) return null
  // htmlContent = testContent

  // 默认组件映射
  const defaultComponentMap: ComponentMap = {
    tip: Tip,
    card: Card,
    boxes: Boxes,
    schema: Schema,
    // 可以在这里添加更多组件类型
    // warning: Warning,
    // info: Info,
  }

  // 合并自定义组件映射
  const componentMap = { ...defaultComponentMap, ...customComponentMap }

  const wrapTablesWithWrapper = (html: string) => {
    if (!html.includes('<table')) return html
    return html
      .replace(/<table([^>]*)>/g, '<div class="table-wrapper"><table$1>')
      .replace(/<\/table>/g, '</table></div>')
  }

  // 解析内容
  const parsedContent = parseContentWithComponents(htmlContent, componentMap)

  const content = (
    <>
      {parsedContent.map((item: ParsedContentItem, index: number) => {
        // 如果是 React 元素，直接渲染
        if (React.isValidElement(item)) {
          const props = item.props as { className?: string }
          // eslint-disable-next-line react/prop-types
          const existingClassName = props?.className || ''
          const newClassName = existingClassName
            ? `${existingClassName} content-component`
            : 'content-component'
          return React.cloneElement(item as React.ReactElement<{ className?: string }>, {
            ...props,
            key: `component-${index}`,
            className: newClassName,
          })
        }
        // 如果是字符串，使用 dangerouslySetInnerHTML 渲染
        if (typeof item === 'string') {
          const processedHtml = wrapTablesWithWrapper(item)
          return (
            <div
              key={`html-${index}`}
              dangerouslySetInnerHTML={{ __html: processedHtml }}
              className='content-renderer-wrapper'
            />
          )
        }
        return null
      })}
    </>
  )

  return <ContentWrapper $customCSS={customCSS}>{content}</ContentWrapper>
}
